import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  TextField,
  Grid,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  ContentCopy as DuplicateIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import FacebookAdsService, {
  FacebookConnection,
  CampaignPerformance,
  FacebookCampaign,
} from '../../services/facebookAdsService';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const FacebookAdsCampaigns: React.FC = () => {
  const [connections, setConnections] = useState<FacebookConnection[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<FacebookConnection | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignPerformance[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCampaigns, setSelectedCampaigns] = useState<Set<number>>(new Set());
  const [dateRange, setDateRange] = useState({
    startDate: format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
  });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    objective: 'OUTCOME_TRAFFIC',
    status: 'PAUSED' as 'ACTIVE' | 'PAUSED',
    special_ad_categories: [] as string[],
  });

  useEffect(() => {
    loadConnections();
  }, []);

  useEffect(() => {
    if (selectedConnection) {
      loadCampaigns();
    }
  }, [selectedConnection, dateRange]);

  const loadConnections = async () => {
    try {
      const response = await FacebookAdsService.listConnections();
      if (response.success && response.data) {
        setConnections(response.data);
        if (response.data.length > 0 && !selectedConnection) {
          setSelectedConnection(response.data[0]);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar conexões:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCampaigns = async () => {
    if (!selectedConnection) return;

    setLoading(true);
    try {
      const response = await FacebookAdsService.getCampaignPerformance(selectedConnection.account_id.toString(), {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });

      if (response.success && response.data) {
        setCampaigns(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePause = async (campaignId: number) => {
    try {
      const response = await FacebookAdsService.pauseCampaign(campaignId.toString());
      if (response.success) {
        setSnackbar({ open: true, message: 'Campanha pausada', severity: 'success' });
        loadCampaigns();
      } else {
        setSnackbar({ open: true, message: response.error || 'Erro', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Erro', severity: 'error' });
    }
  };

  const handleActivate = async (campaignId: number) => {
    try {
      const response = await FacebookAdsService.activateCampaign(campaignId.toString());
      if (response.success) {
        setSnackbar({ open: true, message: 'Campanha ativada', severity: 'success' });
        loadCampaigns();
      } else {
        setSnackbar({ open: true, message: response.error || 'Erro', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Erro', severity: 'error' });
    }
  };

  const handleDelete = async (campaignId: number) => {
    if (!window.confirm('Tem certeza que deseja deletar esta campanha?')) return;

    try {
      const response = await FacebookAdsService.deleteCampaign(campaignId.toString());
      if (response.success) {
        setSnackbar({ open: true, message: 'Campanha deletada', severity: 'success' });
        loadCampaigns();
      } else {
        setSnackbar({ open: true, message: response.error || 'Erro', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Erro', severity: 'error' });
    }
  };

  const handleArchive = async (campaignId: number) => {
    try {
      const response = await FacebookAdsService.archiveCampaign(campaignId.toString());
      if (response.success) {
        setSnackbar({ open: true, message: 'Campanha arquivada', severity: 'success' });
        loadCampaigns();
      } else {
        setSnackbar({ open: true, message: response.error || 'Erro', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Erro', severity: 'error' });
    }
  };

  const handleCreateCampaign = async () => {
    if (!selectedConnection) {
      setSnackbar({ open: true, message: 'Selecione uma conta primeiro', severity: 'error' });
      return;
    }

    if (!campaignForm.name.trim()) {
      setSnackbar({ open: true, message: 'Nome da campanha é obrigatório', severity: 'error' });
      return;
    }

    setCreating(true);
    try {
      const response = await FacebookAdsService.createCampaign({
        accountId: selectedConnection.account_id.toString(),
        name: campaignForm.name.trim(),
        objective: campaignForm.objective,
        status: campaignForm.status,
        special_ad_categories: campaignForm.special_ad_categories.length > 0 ? campaignForm.special_ad_categories : undefined,
      });

      if (response.success) {
        setSnackbar({ 
          open: true, 
          message: `Campanha "${campaignForm.name}" criada com sucesso!`, 
          severity: 'success' 
        });
        setCreateDialogOpen(false);
        setCampaignForm({
          name: '',
          objective: 'OUTCOME_TRAFFIC',
          status: 'PAUSED',
          special_ad_categories: [],
        });
        // Aguardar um pouco e recarregar as campanhas
        setTimeout(() => {
          loadCampaigns();
        }, 2000);
      } else {
        setSnackbar({ 
          open: true, 
          message: response.error || 'Erro ao criar campanha', 
          severity: 'error' 
        });
      }
    } catch (error: any) {
      setSnackbar({ 
        open: true, 
        message: error.message || 'Erro ao criar campanha', 
        severity: 'error' 
      });
    } finally {
      setCreating(false);
    }
  };

  const handleDuplicate = async (campaignId: number, name: string) => {
    const newName = prompt(`Nome para a cópia de "${name}":`);
    if (!newName || !newName.trim()) return;

    try {
      const response = await FacebookAdsService.duplicateCampaign(campaignId.toString(), newName.trim());
      if (response.success) {
        setSnackbar({ open: true, message: 'Campanha duplicada', severity: 'success' });
        loadCampaigns();
      } else {
        setSnackbar({ open: true, message: response.error || 'Erro', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Erro', severity: 'error' });
    }
  };

  const handleBulkPause = async () => {
    if (selectedCampaigns.size === 0) return;
    if (!window.confirm(`Pausar ${selectedCampaigns.size} campanha(s)?`)) return;

    try {
      const response = await FacebookAdsService.bulkPauseCampaigns(
        Array.from(selectedCampaigns).map(id => id.toString()),
        selectedConnection!.account_id.toString()
      );
      if (response.success) {
        setSnackbar({ open: true, message: response.message || 'Campanhas pausadas', severity: 'success' });
        setSelectedCampaigns(new Set());
        loadCampaigns();
      } else {
        setSnackbar({ open: true, message: response.error || 'Erro', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Erro', severity: 'error' });
    }
  };

  const handleBulkActivate = async () => {
    if (selectedCampaigns.size === 0) return;
    if (!window.confirm(`Ativar ${selectedCampaigns.size} campanha(s)?`)) return;

    try {
      const response = await FacebookAdsService.bulkActivateCampaigns(
        Array.from(selectedCampaigns).map(id => id.toString()),
        selectedConnection!.account_id.toString()
      );
      if (response.success) {
        setSnackbar({ open: true, message: response.message || 'Campanhas ativadas', severity: 'success' });
        setSelectedCampaigns(new Set());
        loadCampaigns();
      } else {
        setSnackbar({ open: true, message: response.error || 'Erro', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Erro', severity: 'error' });
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE': return 'success';
      case 'PAUSED': return 'warning';
      case 'DELETED': case 'DISAPPROVED': return 'error';
      case 'ARCHIVED': return 'default';
      default: return 'default';
    }
  };

  const getROASColor = (roas: number) => {
    if (roas >= 3) return 'success';
    if (roas >= 1.5) return 'info';
    if (roas >= 1) return 'warning';
    return 'error';
  };

  if (loading && campaigns.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Campanhas
        </Typography>
        <Stack direction="row" spacing={1}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Conta</InputLabel>
            <Select
              value={selectedConnection?.account_id || ''}
              onChange={(e) => {
                const value = e.target.value;
                const conn = connections.find((c) => c.account_id === parseInt(value.toString()));
                setSelectedConnection(conn || null);
              }}
              label="Conta"
            >
              {connections.map((conn) => (
                <MenuItem key={conn.account_id} value={conn.account_id}>
                  {conn.account_name || `Conta ${conn.account_id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)} color="primary">
            Criar Campanha
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadCampaigns} disabled={loading}>
            Atualizar
          </Button>
        </Stack>
      </Box>

      {/* Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Data Inicial"
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Data Final"
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Stack direction="row" spacing={1}>
                {selectedCampaigns.size > 0 && (
                  <>
                    <Button variant="outlined" size="small" onClick={handleBulkPause} startIcon={<PauseIcon />}>
                      Pausar ({selectedCampaigns.size})
                    </Button>
                    <Button variant="outlined" size="small" onClick={handleBulkActivate} startIcon={<PlayIcon />}>
                      Ativar ({selectedCampaigns.size})
                    </Button>
                    <Button variant="outlined" size="small" onClick={() => setSelectedCampaigns(new Set())}>
                      Limpar
                    </Button>
                  </>
                )}
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabela */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedCampaigns.size > 0 && selectedCampaigns.size < campaigns.length}
                  checked={campaigns.length > 0 && selectedCampaigns.size === campaigns.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedCampaigns(new Set(campaigns.map(c => c.campaign_id)));
                    } else {
                      setSelectedCampaigns(new Set());
                    }
                  }}
                />
              </TableCell>
              <TableCell>Campanha</TableCell>
              <TableCell align="right">Status</TableCell>
              <TableCell align="right">Gasto</TableCell>
              <TableCell align="right">Cliques</TableCell>
              <TableCell align="right">CPC</TableCell>
              <TableCell align="right">CTR</TableCell>
              <TableCell align="right">Conversões</TableCell>
              <TableCell align="right">ROAS</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.map((campaign) => (
              <TableRow key={campaign.campaign_id} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedCampaigns.has(campaign.campaign_id)}
                    onChange={(e) => {
                      const newSet = new Set(selectedCampaigns);
                      if (e.target.checked) {
                        newSet.add(campaign.campaign_id);
                      } else {
                        newSet.delete(campaign.campaign_id);
                      }
                      setSelectedCampaigns(newSet);
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {campaign.campaign_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {campaign.objective || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Chip label={campaign.campaign_status} color={getStatusColor(campaign.campaign_status) as any} size="small" />
                </TableCell>
                <TableCell align="right">{formatCurrency(campaign.total_spend)}</TableCell>
                <TableCell align="right">{formatNumber(campaign.total_clicks)}</TableCell>
                <TableCell align="right">{formatCurrency(campaign.avg_cpc)}</TableCell>
                <TableCell align="right">{campaign.avg_ctr.toFixed(2)}%</TableCell>
                <TableCell align="right">{formatNumber(campaign.total_conversions)}</TableCell>
                <TableCell align="right">
                  <Chip label={`${campaign.roas.toFixed(2)}x`} color={getROASColor(campaign.roas) as any} size="small" />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    {campaign.campaign_status === 'ACTIVE' ? (
                      <Tooltip title="Pausar">
                        <IconButton size="small" onClick={() => handlePause(campaign.campaign_id)} color="warning">
                          <PauseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Ativar">
                        <IconButton size="small" onClick={() => handleActivate(campaign.campaign_id)} color="success">
                          <PlayIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Duplicar">
                      <IconButton size="small" onClick={() => handleDuplicate(campaign.campaign_id, campaign.campaign_name)} color="primary">
                        <DuplicateIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Arquivar">
                      <IconButton size="small" onClick={() => handleArchive(campaign.campaign_id)} color="default">
                        <ArchiveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Deletar">
                      <IconButton size="small" onClick={() => handleDelete(campaign.campaign_id)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {campaigns.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography color="text.secondary">Nenhuma campanha encontrada</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de Criar Campanha */}
      <Dialog open={createDialogOpen} onClose={() => !creating && setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Criar Nova Campanha</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Nome da Campanha"
              fullWidth
              required
              value={campaignForm.name}
              onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
              placeholder="Ex: Campanha de Vendas - Verão 2025"
            />

            <FormControl fullWidth required>
              <InputLabel>Objetivo da Campanha</InputLabel>
              <Select
                value={campaignForm.objective}
                onChange={(e) => setCampaignForm({ ...campaignForm, objective: e.target.value })}
                label="Objetivo da Campanha"
              >
                <MenuItem value="OUTCOME_TRAFFIC">Tráfego</MenuItem>
                <MenuItem value="OUTCOME_LEADS">Geração de Leads</MenuItem>
                <MenuItem value="OUTCOME_ENGAGEMENT">Engajamento</MenuItem>
                <MenuItem value="OUTCOME_AWARENESS">Reconhecimento de Marca</MenuItem>
                <MenuItem value="OUTCOME_SALES">Vendas</MenuItem>
                <MenuItem value="OUTCOME_APP_PROMOTION">Promoção de App</MenuItem>
                <MenuItem value="OUTCOME_VIDEO_VIEWS">Visualizações de Vídeo</MenuItem>
                <MenuItem value="OUTCOME_LANDING_PAGE_VIEWS">Visualizações de Página</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Status Inicial</InputLabel>
              <Select
                value={campaignForm.status}
                onChange={(e) => setCampaignForm({ ...campaignForm, status: e.target.value as 'ACTIVE' | 'PAUSED' })}
                label="Status Inicial"
              >
                <MenuItem value="PAUSED">Pausada (recomendado para configurar primeiro)</MenuItem>
                <MenuItem value="ACTIVE">Ativa (começa a gastar imediatamente)</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Categorias Especiais de Anúncio (opcional)</InputLabel>
              <Select
                multiple
                value={campaignForm.special_ad_categories}
                onChange={(e) => setCampaignForm({ ...campaignForm, special_ad_categories: e.target.value as string[] })}
                label="Categorias Especiais de Anúncio (opcional)"
                renderValue={(selected) => (selected as string[]).join(', ')}
              >
                <MenuItem value="HOUSING">Habitação</MenuItem>
                <MenuItem value="EMPLOYMENT">Emprego</MenuItem>
                <MenuItem value="CREDIT">Crédito</MenuItem>
              </Select>
            </FormControl>

            <Alert severity="info">
              <Typography variant="body2">
                <strong>Dica:</strong> É recomendado criar a campanha como "Pausada" primeiro, 
                configurar o AdSet e os Anúncios, e depois ativar quando estiver tudo pronto.
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={creating}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateCampaign}
            disabled={creating || !campaignForm.name.trim() || !campaignForm.objective}
            startIcon={creating ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {creating ? 'Criando...' : 'Criar Campanha'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FacebookAdsCampaigns;


